// TagDropdown.js - Updated for hierarchical domain/tag system
import React, { useState, useRef, useEffect } from 'react';

const TagDropdown = ({ 
  value = '', 
  onChange, 
  placeholder = 'Select option',
  options = [],
  disabled = false,
  type = 'tag', // 'domain' or 'tag'
  columnIndex = 1 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    if (type === 'domain') {
      // For domains, option is just a string
      return option.toLowerCase().includes(searchTerm.toLowerCase());
    } else {
      // For tags, option might be string or object
      const label = typeof option === 'string' ? option : option.label || option.value;
      return label.toLowerCase().includes(searchTerm.toLowerCase());
    }
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    const selectedValue = type === 'domain' ? option : (typeof option === 'string' ? option : option.value);
    onChange(selectedValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  const getDisplayValue = () => {
    if (!value) return null;
    
    if (type === 'domain') {
      return value;
    } else {
      // For tags, find the option that matches the value
      const selectedOption = options.find(opt => 
        typeof opt === 'string' ? opt === value : opt.value === value
      );
      return selectedOption || value;
    }
  };

  const getTagColorClass = (color = 'blue') => {
    const colorMap = {
      green: 'tag-pill-green',
      blue: 'tag-pill-blue', 
      purple: 'tag-pill-purple',
      orange: 'tag-pill-orange',
      red: 'tag-pill-red',
      gray: 'tag-pill-gray',
      yellow: 'tag-pill-yellow',
      cyan: 'tag-pill-cyan'
    };
    return colorMap[color] || 'tag-pill-blue';
  };

  const displayValue = getDisplayValue();

  return (
    <div className="tag-dropdown-container" ref={dropdownRef}>
      <div 
        className={`tag-dropdown-trigger ${disabled ? 'disabled' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) {
            setIsOpen(!isOpen);
          }
        }}
      >
        {displayValue ? (
          <div className="tag-container">
            <span className={`tag-pill ${getTagColorClass(type === 'domain' ? 'purple' : 'blue')}`}>
              {typeof displayValue === 'string' ? displayValue : displayValue.label || displayValue.value}
              {!disabled && (
                <button 
                  className="tag-clear-btn"
                  onClick={handleClear}
                  title="Clear selection"
                >
                  ×
                </button>
              )}
            </span>
          </div>
        ) : (
          <div className={`tag-placeholder ${disabled ? 'disabled' : ''}`}>
            {disabled ? 'Select domain first' : placeholder}
          </div>
        )}
      </div>

      {isOpen && !disabled && (
        <div className="tag-dropdown-menu">
          <div className="tag-search-container">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="tag-search-input"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          <div className="tag-options-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const optionValue = type === 'domain' ? option : (typeof option === 'string' ? option : option.value);
                const optionLabel = type === 'domain' ? option : (typeof option === 'string' ? option : option.label || option.value);
                const optionColor = type === 'domain' ? 'purple' : (typeof option === 'string' ? 'blue' : option.color || 'blue');
                
                return (
                  <div
                    key={`${optionValue}-${index}`}
                    className="tag-option"
                    onClick={() => handleSelect(option)}
                  >
                    <span className={`tag-pill ${getTagColorClass(optionColor)}`}>
                      {optionLabel}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="tag-no-options">
                No options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagDropdown;